import { InternalPackage } from '../../plans/types/Plans'

/** An organization's copy of a catalog package. Stored copies have `id: ""`; `packageId` is the catalog package. */
export type OrganizationPackage = InternalPackage & {
  packageId: string
  purchasedAt: number
  /** 0 when the package doesn't expire. */
  expiration: number
  note: string | null
}

/** A package added in the edit dialog, as legacy builds it: the catalog package without `id`. */
export type AddedOrganizationPackage = Omit<OrganizationPackage, 'id' | 'expiration' | 'note'> & {
  expiration?: number
  note?: string
}

export type SentOrganizationPackage = Omit<OrganizationPackage, 'id'> | AddedOrganizationPackage

export type OrganizationBillingContact = {
  name: string | null
  phone: string | null
  email: string | null
}

/** Text fields that legacy sends as `''` when they are empty. */
export const organizationTextFields = [
  'address1',
  'address2',
  'zipCode',
  'city',
  'country',
  'contactName',
  'contactEmail',
  'contactPhone',
  'billingType',
  'salesLead',
  'site',
  'invoiceCurrency',
  'invoiceFrequency',
  'paymentTerms',
  'vatNumber',
  'billingAddress1',
  'billingAddress2',
  'billingZipCode',
  'billingCity',
  'billingCountry',
] as const

export type OrganizationTextField = (typeof organizationTextFields)[number]

/** An organization as `GET admin/orgs?limit=0` lists it. Many text fields are `null` until the organization is first saved. */
export type InternalOrganization = {
  [field in OrganizationTextField]: string | null
} & {
  id: string
  createdAt: number
  modifiedAt: number
  createdBy: string | null
  modifiedBy: string | null
  name: string
  defaultLanguage: string | null
  billingContact: OrganizationBillingContact | null
  packages: OrganizationPackage[]
  userCount: number
  maxSeats: number
  /** Derived by the backend from the packages on every save: the highest value of any package. */
  maxGameSlots: number
  maxFollowedGames: number
  maxFeatureIdeas: number
  maxQuickViews: number
  followedGames: string[]
  followedGamesMonthCount: number
  featureIdeasCount: number
  trialUsed: boolean
  accountExpiresAt: number | ''
  trialExpiresAt: number | ''
  companyUrl: string | null
  level: number
  credits: number
  creditsPerMonth: number
  creditsRefreshed: number
  hardQuotaLimit: boolean
  monthlyFee: number
  packageId: string | null
  activeStripeSubscription: boolean
  options: Record<string, unknown>
  expirations: Record<string, number>
  quickViews: Record<string, unknown>
}

/** Legacy's update body: the stored organization with the dialog's fields, empty text as `''` and package copies without `id`. */
export type OrganizationUpdate = Omit<InternalOrganization, OrganizationTextField | 'billingContact' | 'packages'> & {
  [field in OrganizationTextField]: string
} & {
  billingContact: OrganizationBillingContact
  packages: SentOrganizationPackage[]
}

/** Legacy's create body: the model defaults, the dialog's fields and every empty text as `''`. */
export type OrganizationCreate = {
  [field in OrganizationTextField]: string
} & {
  credits: number
  creditsPerMonth: number
  packages: SentOrganizationPackage[]
  name: string
  defaultLanguage: string
  accountExpiresAt: ''
  trialExpiresAt: ''
  billingContact: OrganizationBillingContact
  trialUsed?: boolean
  followedGamesMonthCount?: number
  maxSeats?: number
}

export type OrganizationSave = OrganizationUpdate | OrganizationCreate

export enum OrganizationStatus {
  Subscription = 'subscription',
  Trial = 'trial',
  NotAvailable = 'not-available',
  Inactive = 'inactive',
}

/** A user as `GET admin/orgs/{id}/users`, `GET admin/users/list/orphan` and `GET admin/users/list/all` list them. */
export type InternalUser = {
  id: string
  /** The login. Changing `email` doesn't change it. */
  username: string
  email: string
  firstName: string | null
  lastName: string | null
  title: string | null
  jobFunction: string | null
  /** The organization id, `''` for a user without one. */
  organization: string | null
  /** The organization a user was removed from; `''` or `null` otherwise. */
  removedFrom: string | null
  active: boolean
  oadmin: boolean
  /** 0 until the user first logs in ("invite pending"). */
  lastLoginAt: number
  firstLoginAt: number
  createdAt: number
  modifiedAt: number
  updatedAt: number
  createdBy: string | null
  modifiedBy: string | null
  credits: number
  available: number
  quota: number
  spent: number
  transactions: unknown
  stripeId: string | null
  ownGamesOrder: unknown
  followedGamesOrder: unknown
  language: string | null
  hmacHash: string | null
  emailVerificationTask: boolean
}

/** A user without an organization, as `GET admin/users/list/orphan` lists them. */
export type OrphanedUser = InternalUser

/** Legacy's add body for `POST admin/orgs/{id}/users/add`. */
export type OrganizationUserCreate = {
  id: string
  email: string
  password: string
  firstName: string
  lastName: string
  title: string
  active: true
}

export type UserSettings = Record<string, unknown> & { daily_digest?: boolean }
